/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {UiTemplateConfig} from "../generated/UiTemplateConfig";
import * as log from "loglevel";
import {TeamAppsUiContext} from "./TeamAppsUiContext";
import {TeamAppsEvent} from "./util/TeamAppsEvent";
import {UiGridTemplateConfig} from "../generated/UiGridTemplateConfig";
import {createGridTemplateRenderer} from "./util/UiGridTemplates";
import {createTextCellTemplateRenderer} from "./util/UiTextCellTemplates";
import {UiTemplateReferenceConfig} from "../generated/UiTemplateReferenceConfig";
import {UiTextCellTemplateConfig} from "../generated/UiTextCellTemplateConfig";
import {wrapWithDefaultTagWrapper} from "trivial-components";
import {Renderer} from "./Common";

export class TemplateRegistry {

	public static readonly NOOP_RENDERER: Renderer = {
		render: (o: any) => "<div></div>",
		template: null
	};

	public readonly onTemplateRegistered: TeamAppsEvent<string> = new TeamAppsEvent<string>(this);

	private static logger: log.Logger = log.getLogger("TemplateRegistry");
	private templateConfigsByName: { [name: string]: UiTemplateConfig } = {};
	private renderersByName: { [name: string]: Renderer } = {};

	constructor(private context: TeamAppsUiContext) {
	}

	public registerTemplate(name: string, template: UiTemplateConfig): void {
		let oldTemplate = this.templateConfigsByName[name];
		if (oldTemplate != null) {
			TemplateRegistry.logger.warn(`Template with name ${name} is already registered. Overwriting!`)
		}
		this.templateConfigsByName[name] = template;
		this.renderersByName[name] = this.createTemplateRenderer(template);
		this.onTemplateRegistered.fire(name);
	}

	public getTemplateRendererByName(name: string): Renderer {
		let renderer = this.renderersByName[name];
		if (renderer == null) {
			TemplateRegistry.logger.error(`TemplateRegistry: Cannot find template with name ${name}! Returning no-op renderer.`);
			return TemplateRegistry.NOOP_RENDERER;
		}
		return renderer;
	}

	public getRegisteredTemplateConfigs(): { [name: string]: UiTemplateConfig } {
		return {...this.templateConfigsByName};
	}

	public createTemplateRenderer(template: UiTemplateConfig, idPropertyName?: string): Renderer {
		if (isTemplateReference(template)) {
			return this.getTemplateRendererByName(template.templateId);
		} else if (isTextCellTemplate(template)) {
			return createTextCellTemplateRenderer(template, this.context, idPropertyName);
		} else if (isGridTemplate(template)) {
			return createGridTemplateRenderer(template as UiGridTemplateConfig, this.context, idPropertyName);
		}
	}

	public createTemplateRenderers(templates: { [name: string]: UiTemplateConfig }, idPropertyName?: string): { [name: string]: Renderer } {
		return Object.keys(templates).reduce((templateStringMapObject, templateName) => {
			templateStringMapObject[templateName] = this.createTemplateRenderer(templates[templateName], idPropertyName);
			return templateStringMapObject;
		}, <{ [name: string]: Renderer }> {});
	}

	public convertToWrappedTagMustacheTemplates(templates: { [name: string]: UiTemplateConfig }, idPropertyName?: string) {
		return Object.keys(templates).reduce((templateStringMapObject, templateName) => {
			let templateConfig = templates[templateName];
			let textCellTemplateRenderer = this.createTemplateRenderer(templateConfig, idPropertyName);
			templateStringMapObject[templateName] = {
				render: (view) => wrapWithDefaultTagWrapper(textCellTemplateRenderer.render(view)),
				template: templateConfig
			};
			return templateStringMapObject;
		}, <{ [name: string]: Renderer }> {});
	}

}

export function isTemplateReference(template: UiTemplateConfig): template is UiTemplateReferenceConfig {
	return template._type === "UiTemplateReference";
}

export function isTextCellTemplate(template: any): template is UiTextCellTemplateConfig {
	return template._type === 'UiTextCellTemplate';
}

export function isGridTemplate(template: UiTemplateConfig): template is UiGridTemplateConfig {
	return template._type === "UiGridTemplate";
}